import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useUserAgentDetection } from "@shared/hooks/useUserAgentDetection/useUserAgentDetection";
import { formatDate } from "@shared/utils/dates/formatting";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { isSameDay } from "date-fns";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState, forwardRef } from "react";
import { isDateRange, TZDate } from "react-day-picker";

// Chakra v3 Imports
import { Popover, Portal } from "@chakra-ui/react";

import Box, { BoxProps } from "../../layout/Box/Box.web";
import Flex from "../../layout/Flex/Flex.web";
import {
  BASE_SELECTION_EVENT,
  DatePickerMode,
  DatePickerSelection,
  DatePickerSelectionEvent,
} from "../DatePicker/DatePicker.props";
import DatePickerModal from "../DatePickerModal/DatePickerModal.web";
import DatePickerQuickOptions from "../DatePickerQuickOptions/DatePickerQuickOptions.web";
import Input from "../Input/Input.web";
import { InputDatePickerProps } from "./InputDatePicker.props";

// Props for the extracted TriggerContent component
type TriggerContentProps = BoxProps & {
  inputWidth?: string;
  dataTestId?: string;
  selection?: DatePickerSelection;
  isDisabled?: boolean;
  inputValue: string;
  clearSelection: (e?: React.MouseEvent) => void;
  mode?: DatePickerMode | "single" | "multiple" | "range";
  initialValue?: DatePickerSelection;
  placeholder?: string;
};

const TriggerContent = forwardRef<HTMLDivElement, TriggerContentProps>(
  (
    {
      inputWidth,
      dataTestId,
      selection,
      isDisabled,
      inputValue,
      clearSelection,
      mode,
      initialValue,
      placeholder,
      ...props
    },
    ref
  ) => (
    <Box
      position="relative"
      display="inline-block"
      width={inputWidth}
      {...props}
      ref={ref}
    >
      <Flex
        data-testid={dataTestId}
        bg="bg"
        border="1px solid"
        borderColor="gray.400"
        borderRadius="sm"
        direction="row"
        alignItems="center"
        width="100%"
        px="sm"
        pr={selection ? "0px" : "12px"}
        height="element.3xl"
        cursor={isDisabled ? "not-allowed" : "pointer"}
      >
        <Icon.Calendar.Outline color="inherit" />
        <Button
          variant="unstyled"
          flexGrow={1}
          disabled={isDisabled}
          color="fg"
          justifyContent="flex-start"
          pointerEvents={isDisabled ? "none" : "auto"}
        >
          <Text
            data-testid="input-date-picker-menu-button-text"
            textAlign="left"
            fontWeight="400"
            pl="sm"
            color={inputValue ? "font.dark" : "gray.600"}
          >
            {inputValue || placeholder}
          </Text>
        </Button>
        {selection ? (
          <Button.Ghost
            type="button"
            onClick={clearSelection}
            data-testid="date-picker-clear-button"
            zIndex={2}
          >
            {(mode !== DatePickerMode.single ||
              (mode === DatePickerMode.single &&
                !isSameDay(selection as Date, initialValue as Date))) && (
              <Icon
                name="X"
                outline
                height="element.lg"
                width="element.lg"
                colorPalette="currentColor"
              />
            )}
          </Button.Ghost>
        ) : (
          <Icon.ChevronDown color="inherit" />
        )}
      </Flex>
    </Box>
  )
);

TriggerContent.displayName = "TriggerContent";

const DatePicker = dynamic(() => import("../DatePicker/DatePicker.web"), {
  ssr: false,
});

const InputDatePicker = ({
  isDisabled,
  onChangeSelected,
  placeholder = "MM/DD/YYYY",
  customMenuOptions,
  onSetSelection,
  showQuickMenu = false,
  timezone,
  dateFormat = "MMMM d, yyyy",
  mode,
  initialValue,
  "data-testid": dataTestId = "input-date-picker",
  inputWidth = "100%",
  ...inputDatePickerProps
}: InputDatePickerProps): JSX.Element => {
  // Popover state (Desktop)
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Modal state (Mobile)
  const {
    isOpen: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const isMobile = useUserAgentDetection();
  const [selection, setSelection] = useState<DatePickerSelection>(
    inputDatePickerProps.selected || undefined
  );

  const handleDateSelection = useCallback(
    ({ selected, ...rest }: DatePickerSelectionEvent): void => {
      setSelection((selected as DatePickerSelection) || initialValue);
      onChangeSelected?.({
        selected: (selected as DatePickerSelection) || initialValue,
        ...rest,
      });

      if (mode === "single" && selected) {
        onClose(); // Close Popover
      }
    },
    [onChangeSelected, initialValue, mode, onClose]
  );

  const formatDateForDisplay = (date: TZDate): string => {
    return formatDate({
      date,
      timezone: timezone ?? null,
      format: dateFormat,
    });
  };

  const inputValue = useMemo(() => {
    const value = selection || initialValue;
    if (!value) return "";

    if (Array.isArray(value)) {
      return value.reduce((final: string, current, index) => {
        if (value.length === 1)
          return formatDateForDisplay(
            new TZDate(current || new Date(), timezone)
          );
        if (index === 0)
          return formatDateForDisplay(
            new TZDate(current || new Date(), timezone)
          );
        return `${final} - ${formatDateForDisplay(
          new TZDate(current || new Date(), timezone)
        )}`;
      }, "");
    }
    if (isDateRange(value)) {
      return `${formatDateForDisplay(
        new TZDate(value?.from || new Date(), timezone)
      )} - ${formatDateForDisplay(
        new TZDate(value?.to || new Date(), timezone)
      )}`;
    }
    if (value instanceof TZDate) return formatDateForDisplay(value);
    if (value instanceof Date)
      return formatDateForDisplay(new TZDate(value, timezone));

    return "";
  }, [selection, initialValue, timezone, dateFormat]);

  const clearSelection = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setSelection(initialValue);
      onSetSelection?.(initialValue);
      onChangeSelected?.({ ...BASE_SELECTION_EVENT, selected: initialValue });
    },
    [initialValue]
  );

  const nativeInputValue = () => {
    if (selection) return (selection as Date).toISOString().split("T")[0];
    if (initialValue) return (initialValue as Date).toISOString().split("T")[0];
    return "";
  };

  // --- 1. Mobile Native Input (Single Mode) ---
  // If mobile and single mode, return the native HTML date picker immediately.
  if (isMobile && mode === DatePickerMode.single) {
    return (
      <Input
        isDisabled={isDisabled}
        value={nativeInputValue()}
        onChange={(e) => {
          const date = new Date(e.target.value);
          setSelection(date);
          onChangeSelected?.({ ...BASE_SELECTION_EVENT, selected: date });
        }}
        type="date"
        autoComplete="off"
        placeholder={placeholder}
        data-testid="date-picker-input"
      />
    );
  }

  // Common props for TriggerContent
  const triggerProps = {
    inputWidth,
    dataTestId,
    selection,
    isDisabled,
    inputValue,
    clearSelection,
    mode,
    initialValue,
    placeholder,
  };

  // --- 3. Mobile View (Custom Modal) ---
  // If we are on mobile (and not in single mode, which was handled above),
  // we render the Trigger simply with an onClick for the Modal. No Popover.
  if (isMobile) {
    return (
      <>
        {/* Directly attach onOpenModal to the trigger */}
        <TriggerContent {...triggerProps} onClick={onOpenModal} />

        {mode && (
          <DatePickerModal
            mode={mode}
            isOpen={isModalOpen}
            onClose={onCloseModal}
            onChangeSelected={handleDateSelection}
            onApply={onCloseModal}
            {...inputDatePickerProps}
            selected={selection}
          />
        )}
      </>
    );
  }

  // --- 4. Desktop View (Popover) ---
  // If we are on desktop, we wrap the Trigger in Popover.Root
  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => (e.open ? onOpen() : onClose())}
      positioning={{
        placement: "bottom-start",
        offset: { mainAxis: 7, crossAxis: 0 },
      }}
      modal={false} // Prevents focus trap conflicts when inside Modals
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger asChild>
        {/* TriggerContent receives refs and onClick from Popover.Trigger */}
        <TriggerContent {...triggerProps} />
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner zIndex="popover">
          <Popover.Content
            data-testid="input-date-picker-menu-list"
            bg="bg"
            border="none"
            boxShadow="0px 4px 10px rgba(0, 0, 0, 0.25)"
            borderRadius="md"
            width="auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Popover.Body padding="15px 12px">
              <Flex>
                {showQuickMenu && mode && onChangeSelected && (
                  <DatePickerQuickOptions
                    setSelection={setSelection}
                    mode={mode}
                    onChangeSelected={onChangeSelected}
                    customMenuOptions={customMenuOptions}
                  />
                )}
                {mode && (
                  <DatePicker
                    mode={mode}
                    onChangeSelected={handleDateSelection}
                    timeZone={timezone}
                    {...inputDatePickerProps}
                    selected={selection}
                  />
                )}
              </Flex>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default InputDatePicker;
